/**
 * The kitchen order ticket.
 *
 * Deliberately spare, and deliberately large. A cook reads this from a rail
 * across a hot, busy kitchen: the table number and the dish need to be legible
 * at a glance, and prices, tax and customer details are noise that makes the
 * important parts smaller.
 *
 * Modifiers and notes print at nearly full size rather than as fine print,
 * because "no onions" is the whole point of the ticket when it appears.
 */

import { formatTime } from '../../utils/format';

const KitchenTicket = ({ ticket, isPreview = false }) => {
    if (!ticket) return null;

    return (
        <div
            // Flush-left when actually printing, not centred - see the same
            // comment on Receipt.jsx.
            className={`receipt receipt-80 ${isPreview ? 'mx-auto border border-dashed border-slate-300 p-3' : 'p-1'}`}
            style={{ fontSize: '13px' }}
        >
            <p className="text-center text-[1.5em] font-bold leading-tight">
                {ticket.kitchenSection || 'KITCHEN'}
            </p>

            {/* A second or third round at the same table has to be obvious, or
                the kitchen cooks the first round again. */}
            {ticket.kotSequence > 1 && (
                <p className="text-center text-[1.2em] font-bold leading-tight">
                    ROUND {ticket.kotSequence}
                </p>
            )}

            <div className="rule-solid" />

            <table>
                <tbody>
                    <tr>
                        <td className="text-[1.15em] font-bold">
                            {ticket.orderType}
                        </td>
                        <td className="num text-[1.15em] font-bold">
                            {ticket.tableNumber ? `TABLE ${ticket.tableNumber}` : ticket.orderNumber}
                        </td>
                    </tr>
                    <tr>
                        <td>{ticket.kotNumber}</td>
                        <td className="num">{formatTime(ticket.printedAt)}</td>
                    </tr>
                    {ticket.waiterName && (
                        <tr>
                            <td>Waiter</td>
                            <td className="num">{ticket.waiterName}</td>
                        </tr>
                    )}
                    {ticket.guestCount > 1 && (
                        <tr>
                            <td>Guests</td>
                            <td className="num">{ticket.guestCount}</td>
                        </tr>
                    )}
                </tbody>
            </table>

            <div className="rule-solid" />

            <table>
                <tbody>
                    {ticket.items.map((item, index) => (
                        <tr key={index} className="align-top">
                            <td className="w-[15%] text-[1.4em] font-bold">
                                {item.quantity}
                            </td>
                            <td className="pb-2">
                                <div className="text-[1.25em] font-bold leading-tight">
                                    {item.name}
                                </div>
                                {item.modifiers && (
                                    <div className="text-[1.05em] leading-tight">
                                        &gt; {item.modifiers}
                                    </div>
                                )}
                                {item.note && (
                                    <div className="text-[1.05em] font-bold italic leading-tight">
                                        ** {item.note} **
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {ticket.orderNote && (
                <>
                    <div className="rule" />
                    <p className="text-[1.1em] font-bold leading-tight">
                        ORDER NOTE: {ticket.orderNote}
                    </p>
                </>
            )}

            <div className="rule-solid" />
            <div className="h-6" />
        </div>
    );
};

export default KitchenTicket;
